"use client";

import React from 'react';
import { useTranslations } from '@/hooks/useTranslations';
import { LocalizedLink } from './LocalizedLink';
import { getApiBaseUrl } from '../lib/api';
import { clearToken, getToken } from '../lib/auth';

type ReviewFormProps = {
  casinoSlug: string;
};

export function ReviewForm({ casinoSlug }: ReviewFormProps) {
  const { t } = useTranslations();
  const [authorName, setAuthorName] = React.useState('');
  const [rating, setRating] = React.useState(5);
  const [text, setText] = React.useState('');
  const [message, setMessage] = React.useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = React.useState(false);
  const [token, setToken] = React.useState<string | null>(null);
  const [isNameTouched, setIsNameTouched] = React.useState(false);

  React.useEffect(() => {
    setToken(getToken());
  }, []);

  React.useEffect(() => {
    if (!token || isNameTouched || authorName) {
      return;
    }

    const loadProfile = async () => {
      try {
        const response = await fetch(`${getApiBaseUrl()}/auth/me`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (response.status === 401 || response.status === 403) {
          clearToken();
          setToken(null);
        } else if (response.ok) {
          const profile = (await response.json()) as { displayName?: string };
          if (profile.displayName) {
            setAuthorName(profile.displayName);
          }
        }
      } catch {
        // Ignore profile fetch errors.
      }
    };

    loadProfile();
  }, [token, isNameTouched, authorName]);

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    setIsSubmitting(true);
    setMessage(null);

    try {
      const response = await fetch(`${getApiBaseUrl()}/reviews`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: token ? `Bearer ${token}` : '',
        },
        body: JSON.stringify({
          casinoSlug,
          authorName,
          rating,
          text,
        }),
      });

      if (response.status === 401 || response.status === 403) {
        clearToken();
        setToken(null);
      }
      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(errorText || 'Failed to submit review');
      }

      setAuthorName('');
      setRating(5);
      setText('');
      setMessage(t('review.thanksPending'));
    } catch (error) {
      setMessage(
        error instanceof Error ? error.message : t('review.submitError'),
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-3">
      {!token && (
        <div className="rounded-xl border border-[#ffb74d]/30 bg-[#ffb74d]/10 p-3 text-sm text-[#ffb74d]">
          {t('review.pleaseSignIn')} <LocalizedLink className="underline" href="/login">{t('auth.signIn')}</LocalizedLink> {t('review.or')}{' '}
          <LocalizedLink className="underline" href="/register">{t('auth.createAccount')}</LocalizedLink> {t('review.toSubmitReview')}
        </div>
      )}
      <div className="grid gap-3 sm:grid-cols-2">
        {token ? (
          <div className="rounded-full border border-white/10 bg-white/5 px-4 py-2 text-sm text-slate-100">
            {authorName || 'Reviewer'}
          </div>
        ) : (
          <input
            className="rounded-full border border-white/10 bg-white/5 px-4 py-2 text-sm text-slate-100 placeholder:text-slate-500 focus:border-[#ffb74d] focus:outline-none"
            placeholder={t('review.yourName')}
            value={authorName}
            onChange={(event) => {
              setAuthorName(event.target.value);
              setIsNameTouched(true);
            }}
            required
            disabled={!token}
          />
        )}
        {token && (
          <input type="hidden" name="authorName" value={authorName} />
        )}
        <div className="relative">
          <label htmlFor="review-rating" className="sr-only">
            {t('review.stars')}
          </label>
          <select
            id="review-rating"
            className="w-full cursor-pointer appearance-none rounded-full border border-white/10 bg-white/5 px-4 py-2 pr-10 text-sm text-slate-100 transition-colors hover:border-[#ffb74d]/50 focus:border-[#ffb74d] focus:outline-none focus-visible:ring-2 focus-visible:ring-[#ffb74d]/40 disabled:cursor-not-allowed disabled:opacity-50"
            value={rating}
            onChange={(event) => setRating(Number(event.target.value))}
            disabled={!token}
            aria-label={t('review.stars')}
          >
            {[5, 4, 3, 2, 1].map((value) => (
              <option key={value} value={value} className="bg-[#1e2235] text-slate-100">
                {value} {value === 1 ? t('review.star') : t('review.stars')}
              </option>
            ))}
          </select>
          <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-3">
            <svg
              className="h-4 w-4 text-slate-400"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M19 9l-7 7-7-7"
              />
            </svg>
          </div>
        </div>
      </div>
      <div>
        <label htmlFor="review-text" className="sr-only">
          {t('review.writeReview')}
        </label>
        <textarea
          id="review-text"
          className="min-h-[120px] w-full rounded-2xl border border-white/10 bg-white/5 px-4 py-3 text-sm text-slate-100 placeholder:text-slate-500 focus:border-[#ffb74d] focus:outline-none focus-visible:ring-2 focus-visible:ring-[#ffb74d]/40"
          placeholder={t('review.writeReview')}
          value={text}
          onChange={(event) => setText(event.target.value)}
          required
          disabled={!token}
        />
      </div>
      <button
        type="submit"
        className="rounded-full bg-[#ffb74d] px-5 py-2 text-sm font-semibold text-[#4a2800] disabled:opacity-60"
        disabled={isSubmitting || !token}
      >
        {isSubmitting ? t('review.submitting') : t('review.submitReview')}
      </button>
      {message && <p className="text-sm text-slate-300">{message}</p>}
    </form>
  );
}
